import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { resetPasswordData, resetPasswordRequest } from '../../redux/modules/auth/authSlice';
import { toast } from 'react-toastify';
import {
    Box,
    FormControl,
    FormHelperText,
    OutlinedInput,
    Typography,
    CircularProgress,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const PageContainer = styled("div")(({ theme }) => ({
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    "@media (max-width:768px)": {
        padding: theme.spacing(2),
    },
    "@media (max-width:480px)": {
        padding: theme.spacing(1),
        alignItems: "flex-start",
        paddingTop: theme.spacing(4),
    },
}));

const FormCard = styled("div")(({ theme }) => ({
    width: "100%",
    maxWidth: 440,
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(5),
    "@media (max-width:575px)": {
        padding: theme.spacing(3, 2.5),
        borderRadius: 16,
    },
}));

const IconContainer = styled(Box)(({ theme }) => ({
    width: 64,
    height: 64,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(3),
}));

const FormLabel = styled("div")(({ theme }) => ({
    fontSize: "0.875rem",
    fontWeight: 500,
    marginBottom: 8,
    color: theme.palette.text.primary,
}));

const StyledInput = styled(OutlinedInput)(({ theme }) => ({
    borderRadius: 10,
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    transition: "all 150ms ease",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
        transition: "all 150ms ease",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.error.main,
    },
    "& .MuiInputBase-input": {
        padding: "14px 16px",
        fontSize: "16px",
    },
}));

const SubmitButton = styled("button")(({ theme }) => ({
    width: "100%",
    padding: "14px 24px",
    border: "none",
    borderRadius: 10,
    background: theme.palette.accent?.main || theme.palette.primary.main,
    color: "#fff",
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: "normal",
    cursor: "pointer",
    transition: "all 150ms ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 48,
    WebkitTapHighlightColor: "transparent",
    "&:hover": {
        background: theme.palette.accent?.dark || theme.palette.primary.dark,
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "scale(0.98)",
    },
    "&:disabled": {
        opacity: 0.7,
        cursor: "not-allowed",
        transform: "none",
    },
}));

const BackLink = styled(Link)(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    textDecoration: "none",
    fontWeight: 500,
    transition: "color 150ms ease",
    "&:hover": {
        color: theme.palette.primary.main,
    },
}));

const ModeToggle = styled("button")(({ theme, selected }) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "10px 12px",
    borderRadius: 10,
    border: "2px solid",
    borderColor: selected ? theme.palette.primary.main : "transparent",
    backgroundColor: selected ? `rgba(105, 203, 249, 0.1)` : (theme.palette.background.subtle || theme.palette.grey[50]),
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 150ms ease",
    WebkitTapHighlightColor: "transparent",
    "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: `rgba(105, 203, 249, 0.05)`,
    },
}));

const emailSchema = yup.object().shape({
    email: yup.string().email('Invalid email').required('Email is required'),
});

const phoneSchema = yup.object().shape({
    phone: yup.string()
        .required('Phone number is required')
        .matches(/^\+?[\d\s-]{7,15}$/, 'Enter a valid phone number'),
});

const Forgotpassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const { loading, resetPasswordRes } = useSelector((state) => state.auth);
    const [mode, setMode] = useState('email');

    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: yupResolver(mode === 'email' ? emailSchema : phoneSchema)
    });

    useEffect(() => {
        reset();
    }, [mode, reset]);

    useEffect(() => {
        if (!resetPasswordRes) { return; }
        if (resetPasswordRes?.success) {
            navigate(`/login`);
        }
        toast(resetPasswordRes?.message);
        dispatch(resetPasswordData());
    }, [resetPasswordRes, navigate, dispatch]);

    const onSubmit = (data) => {
        if (mode === 'email') {
            dispatch(resetPasswordRequest({ email: data.email.trim().toLowerCase() }));
        } else {
            dispatch(resetPasswordRequest({ phone: data.phone.trim() }));
        }
    };

    return (
        <PageContainer>
            <FormCard>
                <IconContainer>
                    {mode === 'email' ? <EmailIcon sx={{ fontSize: 32 }} /> : <PhoneIcon sx={{ fontSize: 32 }} />}
                </IconContainer>

                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 1,
                        fontSize: { xs: "1.5rem", sm: "2.125rem" },
                    }}
                >
                    Forgot Password?
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                    {mode === 'email'
                        ? "No worries, we'll send you reset instructions."
                        : "We'll send a reset link to your WhatsApp."}
                </Typography>

                <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                    <ModeToggle
                        type="button"
                        selected={mode === 'email'}
                        onClick={() => setMode('email')}
                    >
                        <EmailIcon sx={{ fontSize: 18 }} />
                        Email
                    </ModeToggle>
                    <ModeToggle
                        type="button"
                        selected={mode === 'phone'}
                        onClick={() => setMode('phone')}
                    >
                        <PhoneIcon sx={{ fontSize: 18 }} />
                        Phone
                    </ModeToggle>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ mb: 3 }}>
                        <FormLabel>{mode === 'email' ? 'Email Address' : 'Phone Number'}</FormLabel>
                        <FormControl
                            sx={{ width: "100%" }}
                            error={Boolean(mode === 'email' ? errors.email : errors.phone)}
                        >
                            {mode === 'email' ? (
                                <StyledInput
                                    {...register('email')}
                                    placeholder="Enter your email"
                                    type="email"
                                    fullWidth
                                />
                            ) : (
                                <StyledInput
                                    {...register('phone')}
                                    placeholder="e.g. 6890667 or +592 6890667"
                                    type="tel"
                                    fullWidth
                                />
                            )}
                            {(errors?.email?.message || errors?.phone?.message) && (
                                <FormHelperText>
                                    {errors?.email?.message || errors?.phone?.message}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    <SubmitButton type="submit" disabled={Boolean(loading)}>
                        {loading && <CircularProgress size={18} color="inherit" />}
                        Reset Password
                    </SubmitButton>
                </form>

                <Box sx={{ textAlign: "center", mt: 4 }}>
                    <BackLink to="/login">
                        <ArrowBackIcon fontSize="small" />
                        Back to Sign In
                    </BackLink>
                </Box>
            </FormCard>
        </PageContainer>
    );
};

export default Forgotpassword;
